<template>
  <div class="upload-dialog">
    <v-dialog
      v-show="uploadDialog"
      v-model="uploadDialog"
      persistent
      max-width="600px"
      v-scroll-lock="!uploadDialog"
    >
      <v-card>
        <v-card-title>
          <v-col class="headline font-weight-bold">Upload ...</v-col>
          <v-spacer></v-spacer>
          <v-col class="text-right body-1">
            <span class="error--text">*</span>
            is required.
          </v-col>
        </v-card-title>
        <v-list-item class="px-6">
          <v-list-item-content class="mt-3 px-3">
            <v-form v-model="valid" @submit.prevent>
              <!-- Title -->
              <v-row dense>
                <v-col cols="4" class="pb-5">
                  Title
                  <span class="error--text">*</span>
                </v-col>
                <v-col cols="8">
                  <v-text-field
                    v-model="newBrain.title.value"
                    outlined
                    dense
                    name="title_new"
                    placeholder="Title"
                    :rules="newBrain.title.rules"
                  ></v-text-field>
                </v-col>
              </v-row>
              <!-- Strain -->
              <v-row dense>
                <v-col cols="4" class="pb-5">Strain</v-col>
                <v-col cols="8">
                  <v-select
                    v-model="newBrain.strain.value"
                    :items="conditions.mouseLines.items"
                    v-if="conditions.mouseLines.items"
                    name="Strain"
                    item-text="Title"
                    item-value="ItemId"
                  >
                    <template v-slot:selection="{item}">
                      <v-chip
                        :color="'#' + item.Color"
                        :dark="!isTextColorDark('#' + item.Color)"
                        class="ma-1"
                      >
                        {{ item.Title }}
                      </v-chip>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <!-- Age -->
              <v-row dense>
                <v-col cols="4" class="pb-5">Age</v-col>
                <v-col cols="8">
                  <v-row>
                    <v-col class="py-0">
                      <v-text-field
                        v-model="newBrain.age.value"
                        outlined
                        dense
                        name="age_new"
                        :rules="newBrain.age.rules"
                        label="Age"
                      ></v-text-field>
                    </v-col>
                    <v-col align-self="center" class="py-0">
                      <v-select
                        v-model="newBrain.age.period"
                        :items="['days', 'weeks', 'months']"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <!-- CellLabels -->
              <v-row dense>
                <v-col cols="4" class="pb-5">Cell Labels</v-col>
                <v-col cols="8">
                  <v-select
                    v-model="newBrain.cellLabels.value"
                    :items="conditions.cellLabels.items"
                    v-if="conditions.cellLabels.items"
                    chips
                    multiple
                    name="cellLabels"
                    item-text="Title"
                    item-value="ItemId"
                  >
                    <template v-slot:selection="{item}">
                      <v-chip
                        :color="'#' + item.Color"
                        :dark="!isTextColorDark('#' + item.Color)"
                        class="ma-1"
                      >
                        {{ item.Title }}
                      </v-chip>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <!-- ExperimentLabels -->
              <v-row dense>
                <v-col cols="4" class="pb-5">Experiment Labels</v-col>
                <v-col cols="8">
                  <v-select
                    v-model="newBrain.experimentLabels.value"
                    :items="conditions.experimentLabels.items"
                    v-if="conditions.experimentLabels.items"
                    chips
                    multiple
                    name="experimentLabels"
                    item-text="Title"
                    item-value="ItemId"
                  >
                    <template v-slot:selection="{item}">
                      <v-chip
                        :color="'#' + item.Color"
                        :dark="!isTextColorDark('#' + item.Color)"
                        class="ma-1"
                      >
                        {{ item.Title }}
                      </v-chip>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <!-- Notes -->
              <v-row dense>
                <v-col cols="4" class="pb-5">Notes</v-col>
                <v-col cols="8">
                  <v-textarea
                    outlined
                    v-model="newBrain.notes.value"
                    name="notes_new"
                    :rules="newBrain.notes.rules"
                    label="Notes"
                  ></v-textarea>
                </v-col>
              </v-row>
              <!-- StructureImage -->
              <v-row dense>
                <v-col cols="4" class="pb-5">
                  Structure Image
                  <span class="error--text">*</span>
                </v-col>
                <v-col cols="8">
                  <v-file-input
                    chips
                    multiple
                    show-size
                    label="Choose file..."
                    accept=".tif,.gz,.nii"
                    v-model="newBrain.structureImage.file"
                    name="StructureImage"
                    prepend-icon="mdi-rotate-3d"
                  ></v-file-input>
                  <span v-if="irregularExtension" class="error--text">
                    You can only upload ".tif", ".nii.gz", or ".nii" files.
                  </span>
                </v-col>
              </v-row>
              <!-- CellTable -->
              <v-row dense>
                <v-col cols="4" class="pb-5">
                  Cell Table
                  <span class="error--text">*</span>
                </v-col>
                <v-col cols="8">
                  <v-file-input
                    chips
                    multiple
                    show-size
                    label="Choose file..."
                    accept=".csv"
                    v-model="newBrain.cellTable.file"
                    name="CellTable"
                    prepend-icon="mdi-table-large"
                  ></v-file-input>
                </v-col>
              </v-row>
              <!-- Registration parameters -->
              <v-row justify="center">
                <v-expansion-panels accordion>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <span class="font-weight-bold">Advanced Parameters</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row dense>
                        <v-col cols="6" class="pb-5">
                          Registration parameters
                        </v-col>
                        <v-col cols="6" align-self="center" class="py-0">
                          <v-select
                            v-model="newBrain.registrationParameters"
                            :items="registrationParameters"
                          ></v-select>
                        </v-col>
                      </v-row>
                      <!-- Table format -->
                      <v-row dense>
                        <v-col cols="6" class="pb-5">
                          Cell Table Format
                        </v-col>
                        <v-col cols="6" align-self="center" class="py-0">
                          <v-select
                            v-model="newBrain.tableFormat"
                            :items="tableFormatOptions"
                          ></v-select>
                        </v-col>
                      </v-row>
                      <v-row dense>
                        <v-col cols="4" class="pb-5">
                          Cell Table Pixel Size
                          <br />
                          (Only applicable to "ilastik" table format)
                        </v-col>
                        <v-col cols="8">
                          <v-row>
                            <v-col class="py-0">
                              <v-text-field
                                v-model="newBrain.tablePixelSizeX.value"
                                outlined
                                dense
                                name="age_new"
                                type="number"
                                label="X"
                              ></v-text-field>
                            </v-col>
                            <v-col class="py-0">
                              <v-text-field
                                v-model="newBrain.tablePixelSizeY.value"
                                outlined
                                dense
                                name="age_new"
                                type="number"
                                label="Y"
                              ></v-text-field>
                            </v-col>
                            <v-col class="py-0">
                              <v-text-field
                                v-model="newBrain.tablePixelSizeZ.value"
                                outlined
                                dense
                                name="age_new"
                                type="number"
                                label="Z"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-row>
            </v-form>
            <!-- Uploads -->
            <v-row>
              <v-col v-if="!uploadReady" class="mt-4 text-center">
                <v-btn
                  v-if="!canUpload"
                  color="disabled"
                  disabled
                  class="white--text"
                  large
                >
                  Upload
                </v-btn>
                <v-btn
                  v-else
                  color="success"
                  @click="upload()"
                  class="white--text"
                  large
                >
                  Upload
                </v-btn>
              </v-col>
              <v-col v-else class="mt-4 text-center">
                <v-progress-circular
                  indeterminate
                  color="primary"
                ></v-progress-circular>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="text-right">
                <v-btn
                  color="error"
                  :disabled="uploadReady"
                  @click="setUploadDialog(false)"
                >
                  Cancel
                </v-btn>
              </v-col>
            </v-row>
          </v-list-item-content>
        </v-list-item>
      </v-card>
    </v-dialog>

    <!-- Change Password dialog -->
    <upload-progress-dialog
      :uploadProgressDialog="uploadProgressDialog"
      :imageUploadProgress="imageUploadProgress"
      :tableUploadProgress="tableUploadProgress"
      @setUploadProgressDialog="setUploadProgressDialog"
      @cancelUpload="cancelUpload"
    ></upload-progress-dialog>
    <confirm-dialog ref="confirm"></confirm-dialog>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import axios from 'axios'

import ConfirmDialog from '@/components/ConfirmDialog.vue'
import UploadProgressDialog from '@/components/brain/UploadProgressDialog.vue'

export default {
  name: 'uploadDialog',
  components: {
    ConfirmDialog,
    UploadProgressDialog,
  },
  props: {
    uploadDialog: {
      type: Boolean,
      default: false,
    },
    conditions: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      source: axios.CancelToken.source(),
      newBrain: {
        id: {
          // Created when registered.
          value: null,
        },
        title: {
          value: null,
          rules: [
            (v) => !v || /^[ -~]*$/i.test(v) || 'Invalid characters.',
            (v) =>
              !v || v.length <= 128 || 'Please enter within 128 characters.',
          ],
        },
        strain: {
          value: null,
        },
        age: {
          value: null,
          period: 'days',
          rules: [
            (v) => !v || /^[0-9]*$/i.test(v) || 'Only numbers can be used.',
            (v) => !v || v.length <= 4 || 'Please enter within 4 characters.',
          ],
        },
        cellLabels: {
          value: null,
        },
        experimentLabels: {
          value: null,
        },
        notes: {
          value: null,
          rules: [
            (v) => !v || /^[ -~\n\r]*$/i.test(v) || 'Invalid characters.',
            (v) =>
              !v || v.length <= 2048 || 'Please enter within 2048 characters.',
          ],
        },
        structureImage: {
          value: null,
          file: null,
        },
        cellTable: {
          value: null,
          file: null,
        },
        registrationParameters: 'default',
        tableFormat: 'standard',
        tablePixelSizeX: {
          value: null,
        },
        tablePixelSizeY: {
          value: null,
        },
        tablePixelSizeZ: {
          value: null,
        },
      },
      uploadProgressDialog: false,
      uploadReady: false,
      imageUploadProgress: 0,
      tableUploadProgress: 0,
      valid: true,
      tableFormatOptions: [
        {
          text: 'Standard',
          value: 'standard',
        },
        {
          text: 'ilastik',
          value: 'ilastik',
        },
      ],
      registrationParameters: [
        {
          text: 'Default',
          value: 'default',
        },
        {
          text: 'Quick',
          value: 'quick',
        },
      ],
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
    irregularExtension: function() {
      return (
        this.newBrain.structureImage.file &&
        this.newBrain.structureImage.file[0] &&
        this.newBrain.structureImage.file[0].name.endsWith('.gz') &&
        !this.newBrain.structureImage.file[0].name.endsWith('.nii.gz')
      )
    },
    canUpload: function() {
      return (
        !this.irregularExtension &&
        this.newBrain.title.value &&
        this.newBrain.structureImage.file &&
        this.newBrain.structureImage.file[0] &&
        this.newBrain.cellTable.file &&
        this.newBrain.cellTable.file[0] &&
        this.valid
      )
    },
  },
  methods: {
    async cancelUpload() {
      if (
        await this.$refs.confirm.open('Are you sure that you want to quit?')
      ) {
        this.source.cancel()
      }
    },
    async setUploadDialog(show) {
      if (
        await this.$refs.confirm.open('Are you sure that you want to quit?')
      ) {
        this.$emit('setUploadDialog', show)
      }
    },
    setUploadProgressDialog(show) {
      this.uploadProgressDialog = show
    },
    async upload() {
      this.newBrain.id.value = null
      this.uploadReady = true
      const result = await this.registerBrain(this.newBrain)
      if (!result) {
        this.uploadReady = false
        return
      }
      this.$emit('setUploadDialog', false)
      // Upload files.
      this.newBrain.id.value = result.data.ItemId
      this.imageUploadProgress = 0
      this.tableUploadProgress = 0
      // Register the new brain.
      this.setUploadProgressDialog(true)
      try {
        const cancelToken = this.source.token
        const configImage = {
          onUploadProgress: function(progressEvent) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            this.imageUploadProgress = percentCompleted
          }.bind(this),
          cancelToken: cancelToken,
        }
        const configTable = {
          onUploadProgress: function(progressEvent) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            this.tableUploadProgress = percentCompleted
          }.bind(this),
          cancelToken: cancelToken,
        }
        await this.uploadFile(
          result.data.ImageUploadFields,
          this.newBrain.structureImage.file[0],
          result.data.ImageUploadUrl,
          configImage
        )
        await this.uploadFile(
          result.data.TableUploadFields,
          this.newBrain.cellTable.file[0],
          result.data.TableUploadUrl,
          configTable
        )
        this.$emit('initBrains')
      } catch (error) {
        // Errors are thrown when upload is canceled.
        this.setUploadProgressDialog(false)
        if (this.newBrain.id.value) {
          await this.deleteBrain(this.newBrain.id.value, true)
        }
        this.$emit('initBrains')
      }
      this.uploadReady = false
    },
  },
  mounted() {},
}
</script>

<style scoped>
/* >>>.v-text-field__details{
  display: none;
} */
.v-form > div {
  align-items: center;
}
.v-select {
  margin-top: 0;
  padding-top: 0;
}
.v-chip--disabled {
  opacity: 1;
}
</style>
